import { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { Dialog, DialogContent, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { apiGet, apiPut } from '@/lib/api';
import { Hint, InfoDot } from '@/components/ui/hint';
import { HELP } from '@/lib/helpText';
import { useScreenTip } from '@/hooks/useScreenTip';
import { fetchCurriculum, setExamSubjects } from '@/lib/premiumExams';
import ConfidencePicker from '@/components/premium/planner/ConfidencePicker';
import { ChevronRight, ChevronDown, ChevronUp, ChevronLeft, Loader2, AlertTriangle, Trash2, Plus, FileText, Check, BookOpen } from 'lucide-react';

/**
 * Portion Mapper (Phase 03).
 * Left panel: Chapter → Topic → Concept tree with a checkbox at every level.
 * Right panel: one exam-paper card per subject listing the mapped nodes.
 * Selection + "Add to exam" is the primary path (never drag-only); every
 * control is a real button/checkbox so keyboard-only works, and the layout
 * stacks at 360px. Feasibility is computed server-side and only warns.
 */
export function PortionMapperDialog({ open, onOpenChange, exam, onSaved }) {
  const subjects = (exam?.subjects || []).map(s => s.subject);
  const [step, setStep] = useState(1);
  const [curriculum, setCurriculum] = useState(null);
  const [picked, setPicked] = useState([]);
  const [savingSubjects, setSavingSubjects] = useState(false);
  const [subject, setSubject] = useState('');
  const [tree, setTree] = useState(null);
  const [expanded, setExpanded] = useState({});
  const [checked, setChecked] = useState({});      // node_id → true
  const [items, setItems] = useState([]);          // mapped items (right panel)
  const [feasibility, setFeasibility] = useState(null);
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);
  useScreenTip('portion-mapper', HELP.tips.mapper, open);

  useEffect(() => {
    if (!open) return;
    const existing = (exam?.subjects || []).map(s => s.subject);
    setSubject(existing[0] || '');
    setPicked(existing);
    setStep(1);
    setCurriculum(null);
    fetchCurriculum(exam?.board || '', exam?.class_level || '')
      .then(setCurriculum)
      .catch(() => setCurriculum({ subjects: [] }));
    // Keyed on the exam id only: re-saving the subjects refreshes the `exam`
    // prop and must NOT bounce the user back to step 1.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, exam?.exam_id]);

  const confidence = ((exam?.subjects || []).find(s => s.subject === subject) || {}).confidence || {};

  const saveConfidence = async (chapter, level) => {
    try {
      const updated = await apiPut(`/premium/v1/exams/${exam.exam_id}/confidence`, { subject, chapter, level });
      onSaved?.(updated);
      toast.message(level === 'weak' ? `${chapter} gets 30 minutes more in the plan`
        : level === 'strong' ? `${chapter} trimmed a little in the plan` : `${chapter} back to normal time`);
    } catch (e) { toast.error(e?.message || 'Could not save that'); }
  };

  const saveSubjects = async () => {
    if (!picked.length) { toast.error('Tick at least one subject for this exam'); return; }
    setSavingSubjects(true);
    try {
      const saved = await setExamSubjects(exam.exam_id, { subjects: picked });
      onSaved?.(saved);
      setSubject(picked.slice().sort((a, b) => a.toLowerCase().localeCompare(b.toLowerCase()))[0]);
      setStep(2);
    } catch (e) { toast.error(e?.message || 'Could not save the subjects'); }
    setSavingSubjects(false);
  };

  useEffect(() => {
    if (!open || step !== 2 || !subject || !exam) return;
    setTree(null); setError(''); setChecked({});
    apiGet(`/premium/v1/exams/${exam.exam_id}/tree?subject=${encodeURIComponent(subject)}`)
      .then(t => {
        setTree(t);
        // Trust the server's stored order; only fall back to walking the tree
        // for portions saved before ordering existed.
        if ((t.items || []).length) {
          setItems(t.items.map(i => ({ ...i, notes: i.notes || '' })));
        } else {
          const existing = [];
          (t.nodes || []).forEach(ch => {
            if (ch.inclusion) existing.push({ node_type: 'chapter', node_id: ch.node_id, label: ch.label, inclusion: ch.inclusion, notes: ch.notes || '' });
            (ch.topics || []).forEach(tp => {
              if (tp.inclusion) existing.push({ node_type: 'topic', node_id: tp.node_id, label: tp.label, inclusion: tp.inclusion, notes: tp.notes || '' });
              (tp.concepts || []).forEach(c => {
                if (c.inclusion) existing.push({ node_type: 'concept', node_id: c.node_id, label: c.label, inclusion: c.inclusion, notes: c.notes || '' });
              });
            });
          });
          setItems(existing);
        }
      })
      .catch(() => setError('We could not load the syllabus tree. Please try again.'));
  }, [open, step, subject, exam]);

  const flatten = (node) => {
    const out = [{ node_type: node.node_type, node_id: node.node_id, label: node.label }];
    (node.topics || []).forEach(tp => {
      // Skip the redundant topic wrapper when it holds one identically named
      // concept — otherwise the paper card lists the same label twice.
      const solo = (tp.concepts || []).length === 1 && tp.concepts[0].label === tp.label;
      if (solo) out.push({ node_type: 'concept', node_id: tp.concepts[0].node_id, label: tp.concepts[0].label });
      else out.push(...flatten({ ...tp, node_type: 'topic' }));
    });
    (node.concepts || []).forEach(c => out.push({ node_type: 'concept', node_id: c.node_id, label: c.label }));
    return out;
  };

  const toggleCheck = (node) => {
    // Ticking a chapter ticks every descendant — no hidden partial state.
    const all = flatten(node);
    const next = { ...checked };
    const turningOn = !checked[node.node_id];
    all.forEach(n => { turningOn ? next[n.node_id] = true : delete next[n.node_id]; });
    setChecked(next);
  };

  const addChecked = () => {
    const ids = Object.keys(checked);
    if (!ids.length) { toast.message('Tick a chapter, topic or concept first'); return; }
    const lookup = {};
    (tree?.nodes || []).forEach(ch => flatten(ch).forEach(n => { lookup[n.node_id] = n; }));
    const additions = ids.filter(id => !items.some(i => i.node_id === id))
      .map(id => ({ ...lookup[id], inclusion: 'FULL', notes: '' }));
    setItems(prev => [...prev, ...additions]);
    setChecked({});
    toast.success(`${additions.length} node${additions.length === 1 ? '' : 's'} added to the paper`);
  };

  const setItem = (id, patch) => setItems(prev => prev.map(i => (i.node_id === id ? { ...i, ...patch } : i)));
  const removeItem = (id) => setItems(prev => prev.filter(i => i.node_id !== id));

  // Order is part of the portion, so it must be changeable without a mouse.
  const move = (id, dir) => setItems(prev => {
    const at = prev.findIndex(i => i.node_id === id);
    const to = at + dir;
    if (at < 0 || to < 0 || to >= prev.length) return prev;
    const next = [...prev];
    [next[at], next[to]] = [next[to], next[at]];
    return next;
  });

  // Drag-and-drop, offered ALONGSIDE the checkbox route (never instead of it).
  const [dragId, setDragId] = useState(null);
  const dropOn = (targetId) => setItems(prev => {
    if (!dragId || dragId === targetId) return prev;
    const from = prev.findIndex(i => i.node_id === dragId);
    const to = prev.findIndex(i => i.node_id === targetId);
    if (from < 0 || to < 0) return prev;
    const next = [...prev];
    next.splice(to, 0, next.splice(from, 1)[0]);
    return next;
  });

  // Dragging a node out of the syllabus tree straight onto the paper.
  const dropFromTree = (payload) => {
    try {
      const n = JSON.parse(payload);
      if (!n?.node_id) return;
      setItems(prev => (prev.some(i => i.node_id === n.node_id)
        ? prev
        : [...prev, { node_type: n.node_type, node_id: n.node_id, label: n.label, inclusion: 'FULL', notes: '' }]));
    } catch { /* not one of ours */ }
  };

  const save = async () => {
    setSaving(true);
    try {
      const res = await apiPut(`/premium/v1/exams/${exam.exam_id}/portion-items`, { subject, items });
      setFeasibility(res.feasibility);
      toast.success(`Portion saved (version ${res.portion_version})`);
      onSaved?.(res.exam);
      if (res.feasibility?.warning) toast.warning(res.feasibility.warning);
    } catch (e) { toast.error(e?.message || 'Could not save the portion'); }
    setSaving(false);
  };

  const partialParents = new Set(items.filter(i => i.inclusion === 'PARTIAL').map(i => i.node_id));
  const catalogue = curriculum?.subjects || [];

  if (step === 1) {
    return (
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="max-w-2xl rounded-3xl max-h-[92vh] overflow-y-auto" data-testid="portion-mapper-dialog">
          <p className="text-[10px] font-heading uppercase tracking-widest text-indigo-500">Step 1 of 2</p>
          <DialogTitle className="font-heading text-xl font-bold text-slate-900">Which subjects are in this exam?</DialogTitle>
          <DialogDescription className="text-sm text-slate-500 font-body">
            Tick every subject {exam?.name ? `“${exam.name}”` : 'this exam'} has a paper for. Next, you will tick the chapters each of those papers covers.
          </DialogDescription>
          <ul className="text-xs text-slate-500 font-body list-disc pl-5 space-y-0.5">
            <li>Only subjects with an approved syllabus can be ticked.</li>
            <li>Un-ticking a subject later removes its chapters from the portion.</li>
          </ul>

          <div className="mt-3 space-y-1.5" data-testid="mapper-subject-picker">
            {!curriculum ? (
              <p className="flex items-center gap-2 text-xs text-slate-400 font-body"><Loader2 className="w-3.5 h-3.5 animate-spin" /> Loading the approved syllabus…</p>
            ) : catalogue.length === 0 ? (
              <p className="text-xs text-slate-500 font-body" data-testid="mapper-catalogue-empty">
                No approved subjects for this board and class yet — ask your admin to publish the syllabus.
              </p>
            ) : catalogue.map(s => {
              const count = (s.chapters || []).length;
              const on = picked.includes(s.name);
              const disabled = count === 0;
              return (
                <button key={s.name} type="button" disabled={disabled}
                  data-testid={`pick-subject-${s.name.replace(/\s+/g, '-').toLowerCase()}`}
                  onClick={() => setPicked(prev => (on ? prev.filter(x => x !== s.name) : [...prev, s.name]))}
                  className={`w-full flex items-center gap-2.5 text-left px-3 py-2.5 rounded-xl border transition-colors ${
                    disabled ? 'border-slate-100 bg-slate-50/60 cursor-not-allowed'
                      : on ? 'border-indigo-300 bg-indigo-50/60' : 'border-slate-200 hover:bg-slate-50'}`}>
                  <span className={`w-4 h-4 rounded border flex items-center justify-center flex-shrink-0 ${on ? 'bg-indigo-500 border-indigo-500 text-white' : 'border-slate-300'}`}>
                    {on && <Check className="w-3 h-3" />}
                  </span>
                  <span className="flex-1 min-w-0">
                    <span className="block font-heading text-sm text-slate-800 truncate">{s.name}</span>
                    <span className="block text-[11px] font-body text-slate-400">
                      {disabled ? 'No approved chapters yet' : `${count} chapter${count === 1 ? '' : 's'} available`}
                    </span>
                  </span>
                  <BookOpen className="w-3.5 h-3.5 text-slate-300 flex-shrink-0" />
                </button>
              );
            })}
          </div>

          <div className="flex items-center justify-between gap-2 mt-4">
            <span className="text-[11px] font-body text-slate-400" data-testid="mapper-picked-count">
              {picked.length} subject{picked.length === 1 ? '' : 's'} selected
            </span>
            <span className="flex items-center gap-2">
              <Button variant="outline" size="sm" onClick={() => onOpenChange(false)} data-testid="mapper-close-btn">Close</Button>
              <Button size="sm" onClick={saveSubjects} disabled={savingSubjects} data-testid="mapper-subjects-next-btn"
                className="bg-gradient-to-r from-indigo-500 to-violet-500 text-white">
                {savingSubjects ? <Loader2 className="w-3.5 h-3.5 animate-spin mr-1" /> : null}
                Next: map the chapters
              </Button>
            </span>
          </div>
        </DialogContent>
      </Dialog>
    );
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-4xl rounded-3xl max-h-[92vh] overflow-y-auto" data-testid="portion-mapper-dialog">
        <p className="text-[10px] font-heading uppercase tracking-widest text-indigo-500">Step 2 of 2</p>
        <DialogTitle className="font-heading text-xl font-bold text-slate-900">Map the chapters for each subject</DialogTitle>
        <DialogDescription className="text-sm text-slate-500 font-body">
          Pick a subject, tick the chapters (or the topics inside them) that are in its paper, add them to the paper card and save. Mark how confident you feel — <span className="font-heading text-rose-600">Weak</span> chapters get more time in your plan.
        </DialogDescription>

        <div className="flex gap-1.5 flex-wrap mt-1 items-center">
          <button onClick={() => setStep(1)} data-testid="mapper-back-to-subjects"
            className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-[11px] font-heading border border-slate-200 text-slate-600 hover:bg-slate-50">
            <ChevronLeft className="w-3 h-3" /> Subjects
          </button>
          {subjects.map(s => (
            <button key={s} data-testid={`mapper-subject-${s.replace(/\s/g, '-').toLowerCase()}`} onClick={() => setSubject(s)}
              className={`px-2.5 py-1 rounded-full text-[11px] font-heading border transition-colors ${subject === s ? 'bg-slate-900 text-white border-slate-900' : 'border-slate-200 text-slate-600 hover:bg-slate-50'}`}>
              {s}
            </button>
          ))}
          {subjects.length > 1 && <InfoDot text={HELP.mapper.subjectTabs} testId="hint-mapper-subjects" />}
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mt-3">
          {/* LEFT — syllabus tree */}
          <div className="rounded-2xl border border-slate-200 p-3 min-h-[220px]">
            <div className="flex items-center justify-between mb-2">
              <p className="text-[10px] font-heading uppercase tracking-widest text-slate-400 inline-flex items-center gap-1">
                Approved syllabus
                <InfoDot text={HELP.mapper.tree} side="right" testId="hint-mapper-tree" />
              </p>
              <Hint text={HELP.mapper.addToExam}>
                <Button size="sm" className="h-7 text-[11px]" data-testid="mapper-add-btn" onClick={addChecked}>
                  <Plus className="w-3 h-3 mr-1" /> Add to exam
                </Button>
              </Hint>
            </div>
            {error ? (
              <p className="text-xs text-rose-600 font-body" data-testid="mapper-error">{error}</p>
            ) : !subject ? (
              <p className="text-xs text-slate-500 font-body" data-testid="mapper-no-subject">
                No subjects picked yet. Go back to step 1 and tick the subjects in this exam.
              </p>
            ) : !tree ? (
              <p className="flex items-center gap-2 text-xs text-slate-400 font-body" data-testid="mapper-loading"><Loader2 className="w-3.5 h-3.5 animate-spin" /> Loading the syllabus…</p>
            ) : (tree.nodes || []).length === 0 ? (
              <p className="text-xs text-slate-500 font-body" data-testid="mapper-empty">No approved chapters for this subject yet.</p>
            ) : (
              <div className="space-y-0.5">
                {tree.nodes.map(ch => (
                  <div key={ch.node_id}
                    draggable={ch.mappable}
                    onDragStart={e => e.dataTransfer.setData('application/gv-node', JSON.stringify(
                      { node_type: 'chapter', node_id: ch.node_id, label: ch.label }))}
                    title={ch.mappable ? 'Drag onto the exam paper, or use the checkbox' : undefined}>
                    <div className="flex items-center gap-1.5">
                      <button data-testid={`mapper-expand-${ch.node_id}`} aria-label={`Expand ${ch.label}`}
                        onClick={() => setExpanded(e => ({ ...e, [ch.node_id]: !e[ch.node_id] }))}
                        className="w-5 h-5 rounded text-slate-400 hover:bg-slate-100 flex items-center justify-center flex-shrink-0">
                        {expanded[ch.node_id] ? <ChevronDown className="w-3.5 h-3.5" /> : <ChevronRight className="w-3.5 h-3.5" />}
                      </button>
                      <label className="flex items-center gap-1.5 flex-1 min-w-0 cursor-pointer">
                        <input type="checkbox" data-testid={`mapper-check-${ch.node_id}`} checked={!!checked[ch.node_id]}
                          onChange={() => toggleCheck(ch)} className="w-3.5 h-3.5 accent-indigo-500" />
                        <span className="text-[12px] font-body text-slate-700 truncate">{ch.label}</span>
                        {partialParents.has(ch.node_id) && <span className="text-[9px] font-heading uppercase text-amber-600 bg-amber-50 px-1.5 rounded-full flex-shrink-0">partial</span>}
                        {ch.provisional && (
                          <Hint text={ch.origin === 'school_library'
                            ? "Your school's own syllabus, approved by our team — everything is already built for it."
                            : 'From the syllabus you uploaded. Plannable now — the infographics, cinegraphics and puzzles for it are being built (within 48 hours).'}>
                            <span data-testid={`mapper-provisional-${ch.node_id}`} className="text-[9px] font-heading uppercase text-indigo-600 bg-indigo-50 px-1.5 rounded-full flex-shrink-0">
                              {ch.origin === 'school_library' ? 'school syllabus' : 'your upload'}
                            </span>
                          </Hint>
                        )}
                        {!ch.mappable && (
                          <Hint text={HELP.mapper.noSyllabusFile}>
                            <span className="text-[9px] font-body text-slate-400 flex-shrink-0 underline decoration-dotted">no syllabus file</span>
                          </Hint>
                        )}
                      </label>
                      <ConfidencePicker value={confidence[ch.label] || 'ok'}
                        testId={`mapper-confidence-${ch.node_id}`}
                        onChange={(next) => saveConfidence(ch.label, next)} />
                    </div>
                    {expanded[ch.node_id] && (ch.topics || []).map(tp => {
                      // A syllabus heading yields one topic + one identically
                      // named concept. Showing both repeats the same text, so
                      // collapse to the concept row (that is what carries time).
                      const solo = (tp.concepts || []).length === 1 && tp.concepts[0].label === tp.label
                        ? tp.concepts[0] : null;
                      if (solo) {
                        return (
                          <label key={tp.node_id} className="flex items-center gap-1.5 ml-6 mt-0.5 cursor-pointer">
                            <input type="checkbox" data-testid={`mapper-check-${solo.node_id}`} checked={!!checked[solo.node_id]}
                              onChange={() => toggleCheck({ ...solo, node_type: 'concept', topics: [] })} className="w-3.5 h-3.5 accent-fuchsia-500" />
                            <span className="text-[11px] font-body text-slate-600 truncate">{solo.label} · {solo.estimated_minutes}m</span>
                            {partialParents.has(solo.node_id) && <span className="text-[9px] font-heading uppercase text-amber-600 bg-amber-50 px-1.5 rounded-full flex-shrink-0">partial</span>}
                          </label>
                        );
                      }
                      return (
                      <div key={tp.node_id} className="ml-6 mt-0.5">
                        <label className="flex items-center gap-1.5 cursor-pointer">
                          <input type="checkbox" data-testid={`mapper-check-${tp.node_id}`} checked={!!checked[tp.node_id]}
                            onChange={() => toggleCheck({ ...tp, node_type: 'topic' })} className="w-3.5 h-3.5 accent-indigo-500" />
                          <span className="text-[11px] font-body text-slate-600 truncate">{tp.label}</span>
                        </label>
                        {(tp.concepts || []).map(c => (
                          <label key={c.node_id} className="flex items-center gap-1.5 ml-5 cursor-pointer">
                            <input type="checkbox" data-testid={`mapper-check-${c.node_id}`} checked={!!checked[c.node_id]}
                              onChange={() => toggleCheck({ ...c, node_type: 'concept', topics: [] })} className="w-3 h-3 accent-fuchsia-500" />
                            <span className="text-[11px] font-body text-slate-500 truncate">{c.label} · {c.estimated_minutes}m</span>
                          </label>
                        ))}
                      </div>
                      );
                    })}
                    {expanded[ch.node_id] && (ch.topics || []).length === 0 && (
                      <p className="ml-6 text-[10px] text-slate-400 font-body">Syllabus file not attached yet — ask your admin to upload it.</p>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* RIGHT — exam paper card */}
          <div className="rounded-2xl border-2 border-dashed border-indigo-200 bg-indigo-50/30 p-3 transition-colors"
            data-testid="mapper-paper-card"
            onDragOver={e => { e.preventDefault(); e.currentTarget.classList.add('border-indigo-500', 'bg-indigo-50/70'); }}
            onDragLeave={e => e.currentTarget.classList.remove('border-indigo-500', 'bg-indigo-50/70')}
            onDrop={e => {
              e.preventDefault();
              e.currentTarget.classList.remove('border-indigo-500', 'bg-indigo-50/70');
              dropFromTree(e.dataTransfer.getData('application/gv-node'));
            }}>
            <div className="flex items-center gap-1.5 mb-2">
              <FileText className="w-3.5 h-3.5 text-indigo-500" />
              <p className="text-[11px] font-heading text-indigo-800">{subject} paper · {exam?.start_date_ist}</p>
            </div>
            {items.length === 0 ? (
              <p className="text-[11px] text-slate-500 font-body" data-testid="mapper-paper-empty">Nothing mapped yet. Tick nodes on the left and press "Add to exam".</p>
            ) : (
              <div className="space-y-1.5">
                {items.map((i, idx) => (
                  <div key={i.node_id} className="rounded-xl bg-white border border-slate-200 p-2 cursor-grab active:cursor-grabbing"
                    data-testid={`mapper-item-${i.node_id}`}
                    draggable
                    onDragStart={() => setDragId(i.node_id)}
                    onDragEnd={() => setDragId(null)}
                    onDragOver={e => e.preventDefault()}
                    onDrop={e => { e.preventDefault(); dropOn(i.node_id); setDragId(null); }}>
                    <div className="flex items-center gap-1.5">
                      <span className="text-[9px] font-heading uppercase text-slate-400 flex-shrink-0">{i.node_type}</span>
                      <span className="text-[11px] font-body text-slate-700 truncate flex-1">{i.label}</span>
                      <Hint text={HELP.mapper.partial}>
                        <button data-testid={`mapper-partial-${i.node_id}`}
                          onClick={() => setItem(i.node_id, { inclusion: i.inclusion === 'PARTIAL' ? 'FULL' : 'PARTIAL' })}
                          className={`text-[9px] font-heading uppercase px-1.5 py-0.5 rounded-full border flex-shrink-0 ${i.inclusion === 'PARTIAL' ? 'bg-amber-500 border-amber-500 text-white' : 'border-slate-300 text-slate-500'}`}>
                          {i.inclusion}
                        </button>
                      </Hint>
                      <Hint text={HELP.mapper.reorder}>
                        <span className="inline-flex flex-col leading-none flex-shrink-0">
                          <button data-testid={`mapper-up-${i.node_id}`} aria-label={`Move ${i.label} earlier`}
                            disabled={idx === 0} onClick={() => move(i.node_id, -1)}
                            className="w-5 h-3 rounded text-slate-400 hover:text-indigo-600 disabled:opacity-25 flex items-center justify-center">
                            <ChevronUp className="w-3 h-3" />
                          </button>
                          <button data-testid={`mapper-down-${i.node_id}`} aria-label={`Move ${i.label} later`}
                            disabled={idx === items.length - 1} onClick={() => move(i.node_id, 1)}
                            className="w-5 h-3 rounded text-slate-400 hover:text-indigo-600 disabled:opacity-25 flex items-center justify-center">
                            <ChevronDown className="w-3 h-3" />
                          </button>
                        </span>
                      </Hint>
                      <Hint text={HELP.mapper.removeItem}>
                        <button data-testid={`mapper-remove-${i.node_id}`} onClick={() => removeItem(i.node_id)} aria-label={`Remove ${i.label}`}
                          className="w-5 h-5 rounded text-slate-400 hover:text-rose-600 flex items-center justify-center flex-shrink-0">
                          <Trash2 className="w-3 h-3" />
                        </button>
                      </Hint>
                    </div>
                    <Hint text={HELP.mapper.notes} className="w-full">
                      <Input data-testid={`mapper-notes-${i.node_id}`} value={i.notes} placeholder="Notes (e.g. diagrams only)"
                        onChange={e => setItem(i.node_id, { notes: e.target.value })}
                        className="h-7 mt-1.5 text-[11px] rounded-lg bg-slate-50" />
                    </Hint>
                  </div>
                ))}
              </div>
            )}

            {feasibility && (
              <div className={`mt-3 rounded-xl p-2 text-[11px] font-body ${feasibility.feasible ? 'bg-emerald-50 text-emerald-700' : 'bg-amber-50 text-amber-800'}`} data-testid="mapper-feasibility">
                {!feasibility.feasible && <AlertTriangle className="w-3.5 h-3.5 inline mr-1" />}
                {feasibility.concept_count} concept{feasibility.concept_count === 1 ? '' : 's'} mapped · needs {feasibility.required_minutes} min · {feasibility.days_available} days give about {feasibility.usable_capacity_minutes} min after a {feasibility.buffer_pct}% revision buffer.
                {feasibility.warning ? ` ${feasibility.warning}` : ' Comfortably doable.'}
                <InfoDot text={HELP.mapper.feasibility} title="How we work this out" side="top" className="ml-1" testId="hint-feasibility" />
              </div>
            )}
          </div>
        </div>

        <div className="flex items-center justify-end gap-2 mt-3">
          <Button variant="outline" size="sm" onClick={() => onOpenChange(false)} data-testid="mapper-close-btn">Close</Button>
          <Hint text={HELP.mapper.save} side="top" align="end">
            <Button size="sm" onClick={save} disabled={saving} data-testid="mapper-save-btn"
              className="bg-gradient-to-r from-indigo-500 to-violet-500 text-white">
              {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin mr-1" /> : null} Save portion
            </Button>
          </Hint>
        </div>
      </DialogContent>
    </Dialog>
  );
}

export default PortionMapperDialog;
